import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { PRESETS } from '@eip/engine';
import { useStore, withResizeStep } from '../state/store';
import { Select, commitDraft, nextDraft } from './controls';

/** Accepts `#rrggbb` or bare `rrggbb` (which it prefixes); otherwise null. */
function normalizeHex(raw: string): string | null {
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw}`;
  return null;
}

export function SettingsBar() {
  const settings = useStore((s) => s.settings);
  const setSettings = useStore((s) => s.setSettings);
  const steps = useStore((s) => s.steps);
  const setSteps = useStore((s) => s.setSteps);
  const preset = PRESETS.find((p) => p.id === settings.presetId) ?? PRESETS[0];
  const applySize = (widthIn: number | null, dpi: number) => { setSettings({ targetWidthIn: widthIn, dpi }); setSteps(withResizeStep(steps, widthIn, dpi)); };

  // Local draft so every keystroke is visible even while the hex string is
  // mid-edit (e.g. "#12"); only a fully valid hex is pushed to the store.
  // Skipped while focused so an external garmentHex change (e.g. a preset
  // pushing a new default) can't clobber an in-progress edit.
  const [hexDraft, setHexDraft] = useState(settings.garmentHex);
  const hexFocused = useRef(false);
  useEffect(() => { setHexDraft((prev) => nextDraft(prev, settings.garmentHex, hexFocused.current)); }, [settings.garmentHex]);
  const hexValid = normalizeHex(hexDraft) !== null;
  const onHexChange = (raw: string) => {
    setHexDraft(raw);
    const normalized = normalizeHex(raw);
    if (normalized) setSettings({ garmentHex: normalized });
  };

  // Print width and DPI follow the same local-draft-then-commit pattern: typing
  // must not clamp or trigger a run per keystroke (a min-0.25 field jumping to
  // 0.25 at the first digit, or a transient 1-in resize before "12" lands), and
  // the value-sync effect is skipped while focused for the same reason as hex.
  const [widthDraft, setWidthDraft] = useState(settings.targetWidthIn == null ? '' : String(settings.targetWidthIn));
  const widthFocused = useRef(false);
  // Whether the draft has been edited since the last commit/reset: an unedited
  // field must never re-commit a stale draft over an external store write (e.g.
  // a blur firing after a preset change already applied a new width elsewhere).
  const widthDirty = useRef(false);
  useEffect(() => { setWidthDraft((prev) => nextDraft(prev, settings.targetWidthIn == null ? '' : String(settings.targetWidthIn), widthFocused.current)); }, [settings.targetWidthIn]);
  const commitWidth = (raw: string) => {
    if (raw.trim() === '') { setWidthDraft(''); if (settings.targetWidthIn !== null) applySize(null, settings.dpi); return; }
    if (isNaN(Number(raw))) {
      // A non-numeric draft on commit keeps whatever is current, including
      // native (null): commitDraft always needs a numeric fallback, which
      // would otherwise coerce native into an arbitrary width (11) instead
      // of staying native.
      setWidthDraft(settings.targetWidthIn == null ? '' : String(settings.targetWidthIn));
      return;
    }
    const v = commitDraft(raw, settings.targetWidthIn ?? 11, 0.25, preset.maxWidthIn, 0.25);
    setWidthDraft(String(v));
    if (v !== settings.targetWidthIn) applySize(v, settings.dpi);
  };

  const [dpiDraft, setDpiDraft] = useState(String(settings.dpi));
  const dpiFocused = useRef(false);
  const dpiDirty = useRef(false);
  useEffect(() => { setDpiDraft((prev) => nextDraft(prev, String(settings.dpi), dpiFocused.current)); }, [settings.dpi]);
  const commitDpi = (raw: string) => {
    const v = commitDraft(raw, settings.dpi, 72, 600, 1);
    setDpiDraft(String(v));
    if (v !== settings.dpi) applySize(settings.targetWidthIn, v);
  };

  return (
    <>
      <label className="row">Garment
        <input type="color" value={settings.garmentHex} onChange={(e) => setSettings({ garmentHex: e.target.value })} aria-label="Garment colour" />
        <input
          value={hexDraft}
          onFocus={() => { hexFocused.current = true; }}
          onChange={(e) => onHexChange(e.target.value)}
          onBlur={() => { hexFocused.current = false; if (!hexValid) setHexDraft(settings.garmentHex); }}
          aria-invalid={!hexValid}
          style={{ width: 84 }}
          aria-label="Garment hex"
        />
      </label>
      <label className="row">Print width
        <input type="number" min={0} max={preset.maxWidthIn} step={0.25} value={widthDraft} placeholder="native" style={{ width: 72 }}
          onFocus={() => { widthFocused.current = true; }}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setWidthDraft(e.target.value);
            widthDirty.current = true;
            // native step arrows (and the up/down spinner) replace the whole
            // value atomically, unlike a keystroke building up a draft, so
            // that change is safe to commit immediately.
            if ((e.nativeEvent as InputEvent).inputType === 'insertReplacementText') { commitWidth(e.target.value); widthDirty.current = false; }
          }}
          onBlur={() => { widthFocused.current = false; if (widthDirty.current) { commitWidth(widthDraft); widthDirty.current = false; } }}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') { commitWidth(widthDraft); widthDirty.current = false; }
            else if (e.key === 'Escape') { setWidthDraft(settings.targetWidthIn == null ? '' : String(settings.targetWidthIn)); widthDirty.current = false; }
          }}
          aria-label="Target width in inches" /> in
      </label>
      <label className="row">DPI
        <input type="number" min={72} max={600} value={dpiDraft} style={{ width: 64 }}
          onFocus={() => { dpiFocused.current = true; }}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setDpiDraft(e.target.value);
            dpiDirty.current = true;
            if ((e.nativeEvent as InputEvent).inputType === 'insertReplacementText') { commitDpi(e.target.value); dpiDirty.current = false; }
          }}
          onBlur={() => { dpiFocused.current = false; if (dpiDirty.current) { commitDpi(dpiDraft); dpiDirty.current = false; } }}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') { commitDpi(dpiDraft); dpiDirty.current = false; }
            else if (e.key === 'Escape') { setDpiDraft(String(settings.dpi)); dpiDirty.current = false; }
          }}
          aria-label="Target dpi" />
      </label>
      <Select label="Vendor" value={settings.presetId} options={PRESETS.map((p) => ({ value: p.id, label: p.name }))} onChange={(v) => setSettings({ presetId: v })} />
      {preset.notes && <span className="muted small" title={preset.notes}>ⓘ {preset.notes}</span>}
    </>
  );
}
